import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { API, DISTRICTS, AGE_GROUPS, PROFESSIONS, INSTITUTIONS } from './joinConfig';

const inputCls = "w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5 text-base text-gray-900 placeholder-gray-400 outline-none transition-colors duration-200 focus:border-party-green focus:bg-white";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</span>
    {children}
  </label>
);

const Chips = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button type="button" key={o.value} onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${value === o.value ? 'border-party-green bg-party-green text-white' : 'border-gray-200 text-gray-700 hover:border-gray-400'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

export const JoinForm = ({ refCode, onSuccess, initialDistrict = '' }) => {
  const { t } = useLanguage();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState({ full_name: '', mobile: '', age_group: '', district: DISTRICTS.includes(initialDistrict) ? initialDistrict : '', nid: '', dob: '', father_name: '', profession: 'Student', institution: '', present_address: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const next = (e) => {
    e.preventDefault();
    if (!f.age_group) return setError(t({ en: 'Pick your age group', bn: 'আপনার বয়সসীমা নির্বাচন করুন' }));
    setError('');
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...f, education: f.institution || 'N/A', referred_by: refCode || undefined, source: 'join_drive' }),
      });
      if (res.status === 409) {
        setError(t({ en: 'This NID is already registered with another mobile number. Use that number to find your card.', bn: 'এই এনআইডি অন্য একটি মোবাইল নম্বরে নিবন্ধিত। কার্ড পেতে সেই নম্বর ব্যবহার করুন।' }));
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      onSuccess({ ...data, full_name: f.full_name, district: f.district, mobile: f.mobile });
    } catch {
      setError(t({ en: 'Something went wrong. Try again.', bn: 'কিছু ভুল হয়েছে। আবার চেষ্টা করুন।' }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-[2rem] border-t-4 border-party-red bg-white p-6 shadow-2xl sm:p-8" data-testid="join-form-card">
      <div className="mb-6 flex items-center justify-between">
        <p className="font-join text-lg font-bold text-gray-900">{step === 1 ? t({ en: "Let's go", bn: 'চলুন শুরু করি' }) : t({ en: 'Almost there', bn: 'প্রায় শেষ' })}</p>
        <div className="flex gap-1.5" data-testid="join-form-steps">
          {[1, 2].map((s) => <span key={s} className={`h-1.5 w-8 rounded-full transition-colors duration-300 ${step >= s ? 'bg-party-green' : 'bg-gray-200'}`} />)}
        </div>
      </div>
      {step === 1 ? (
        <form onSubmit={next} className="space-y-4" data-testid="join-step-1">
          <Field label={t({ en: 'Full name', bn: 'পূর্ণ নাম' })}><input required className={inputCls} value={f.full_name} onChange={set('full_name')} data-testid="join-fullname" /></Field>
          <Field label={t({ en: 'Mobile number', bn: 'মোবাইল নম্বর' })}><input required inputMode="tel" pattern="01[0-9]{9}" title="01XXXXXXXXX" placeholder="01XXXXXXXXX" className={inputCls} value={f.mobile} onChange={set('mobile')} data-testid="join-mobile" /></Field>
          <Field label={t({ en: 'Age group', bn: 'বয়সসীমা' })}><Chips testid="join-age" options={AGE_GROUPS.map((a) => ({ value: a, label: a }))} value={f.age_group} onChange={set('age_group')} /></Field>
          <Field label={t({ en: 'District', bn: 'জেলা' })}>
            <select required className={`${inputCls} appearance-none`} value={f.district} onChange={set('district')} data-testid="join-district">
              <option value="" className="bg-white">{t({ en: 'Select district', bn: 'জেলা নির্বাচন করুন' })}</option>
              {DISTRICTS.map((d) => <option key={d} value={d} className="bg-white">{d}</option>)}
            </select>
          </Field>
          {error && <p className="text-sm text-party-red" data-testid="join-error">{error}</p>}
          <button type="submit" className="font-join flex w-full items-center justify-center gap-2 rounded-full bg-party-red py-4 text-base font-bold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95" data-testid="join-next-btn">
            {t({ en: 'Next', bn: 'পরবর্তী' })} <ArrowRight className="h-5 w-5" />
          </button>
        </form>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="join-step-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t({ en: 'NID / Birth Reg. No.', bn: 'এনআইডি / জন্ম নিবন্ধন নং' })}><input required inputMode="numeric" className={inputCls} value={f.nid} onChange={set('nid')} data-testid="join-nid" /></Field>
            <Field label={t({ en: 'Date of birth', bn: 'জন্ম তারিখ' })}><input required type="date" className={`${inputCls}`} value={f.dob} onChange={set('dob')} data-testid="join-dob" /></Field>
          </div>
          <Field label={t({ en: "Father's / Mother's name", bn: 'বাবা / মায়ের নাম' })}><input required className={inputCls} value={f.father_name} onChange={set('father_name')} data-testid="join-fathername" /></Field>
          <Field label={t({ en: 'You are a', bn: 'আপনি' })}><Chips testid="join-profession" options={PROFESSIONS.map((p) => ({ value: p.value, label: t(p.label) }))} value={f.profession} onChange={set('profession')} /></Field>
          <Field label={t({ en: 'School / College / University (optional)', bn: 'স্কুল / কলেজ / বিশ্ববিদ্যালয় (ঐচ্ছিক)' })}><input list="ndp-institutions" autoComplete="off" className={inputCls} value={f.institution} onChange={set('institution')} placeholder={t({ en: 'Start typing, or enter your own', bn: 'টাইপ করুন বা নিজের প্রতিষ্ঠানের নাম লিখুন' })} data-testid="join-institution" />
            <datalist id="ndp-institutions">{INSTITUTIONS.map((i) => <option key={i} value={i} />)}</datalist></Field>
          <Field label={t({ en: 'Present address', bn: 'বর্তমান ঠিকানা' })}><input required className={inputCls} value={f.present_address} onChange={set('present_address')} data-testid="join-address" /></Field>
          {error && <p className="text-sm text-party-red" data-testid="join-error">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={() => setStep(1)} className="rounded-full border border-gray-200 px-5 text-gray-700 transition-colors duration-200 hover:bg-gray-100" data-testid="join-back-btn"><ArrowLeft className="h-5 w-5" /></button>
            <button type="submit" disabled={busy} className="font-join flex flex-1 items-center justify-center gap-2 rounded-full bg-party-green py-4 text-base font-bold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60" data-testid="join-submit-btn">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
              {t({ en: 'Join & get my ID', bn: 'যোগ দিন ও আইডি নিন' })}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
